import {
  type FieldValues,
  useController,
  useFormContext,
} from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { type CurrencyInputProps, Input } from '~/chords/Input';

export type FormFieldCurrencyInputProps<TFormFields extends FieldValues> =
  ComponentProps<
    Omit<FormFieldProps<TFormFields>, 'children' | 'triggerClassName'> &
      Omit<
        CurrencyInputProps,
        | 'name'
        | 'value'
        | 'onValueChange'
        | 'state'
        | 'dangerouslyOverrideClassName'
      >
  >;

export function FormFieldCurrencyInput<TFormFields extends FieldValues>({
  name,
  id,
  label,
  labelAsSpan,
  labelAddon,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  fieldMessageClassName,
  showErrorMessage = true,
  ...currencyInputProps
}: FormFieldCurrencyInputProps<TFormFields>) {
  const {
    formState: { isSubmitted },
  } = useFormContext<TFormFields>();

  const {
    field,
    fieldState: { invalid },
  } = useController<TFormFields>({ name });

  const showError = isSubmitted && invalid;

  return (
    <FormField
      name={name}
      id={id}
      fieldMessageClassName={fieldMessageClassName}
      label={label}
      labelAsSpan={labelAsSpan}
      labelAddon={labelAddon}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      showErrorMessage={showErrorMessage}
    >
      <Input.Currency
        {...currencyInputProps}
        id={id || name}
        name={name}
        defaultValue={field.value}
        // https://github.com/cchanxzy/react-currency-input-field?tab=readme-ov-file#onvaluechange
        onValueChange={(_newValue, _name, values) => {
          field.onChange(values?.float ?? 0);
        }}
        onBlur={field.onBlur}
        disabled={disabled}
        readOnly={readOnly}
        state={showError ? 'error' : 'default'}
      />
    </FormField>
  );
}

FormFieldCurrencyInput.displayName = 'FormFieldCurrencyInput';
